import { type FC, useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useObjectIdRouteParam } from 'hooks/useObjectIdRouteParam';
import ImportDetails from 'pages/objectManager/components/JobResults/pages/components/ImportDetails';
import { fetchFields } from 'slices/field.slice';
import { requestObjectTypes } from 'slices/objectTypes.slice';
import { fetchOrganizationHierarchy } from 'slices/organizationHierarchy.slice';
import { useAppDispatch } from 'slices/store';
import {
  isBulkEditJob,
  isDeleteJob,
  isExportJob,
  isImportJob,
} from 'types/objectJob.guards';
import { useFetchBatchEditObjectJobQuery } from '../api/objectJobApi';
import { useJobPollingInterval } from '../hooks/useJobPollingInterval';
import BulkEditDetails from './components/BulkEditDetails';
import DeleteDetails from './components/DeleteDetails';
import ExportDetails from './components/ExportDetails';

const JobDetailPage: FC = () => {
  const id = useObjectIdRouteParam();

  const { pollingInterval, setPollingActive } = useJobPollingInterval();
  const { data, error, isFetching } = useFetchBatchEditObjectJobQuery(
    id ? { id } : skipToken,
    { pollingInterval, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (data) {
      setPollingActive(!['FINISHED', 'FAILED'].includes(data.status));
    }
  }, [data?.status, setPollingActive]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchFields());
    dispatch(fetchOrganizationHierarchy());
    dispatch(requestObjectTypes());
  }, [dispatch]);

  if (!id) {
    return <>Error: Not found</>;
  }

  if (isFetching && !data) {
    return <>Loading</>;
  }

  if (error) {
    return (
      <>
        Error: {'data' in error ? (error.data as any).message : 'Unknown error'}
      </>
    );
  }

  if (!data) {
    return <>Error: Not found</>;
  }

  if (isBulkEditJob(data)) {
    return <BulkEditDetails data={data} />;
  }

  if (isExportJob(data)) {
    return <ExportDetails data={data} />;
  }

  if (isDeleteJob(data)) {
    return <DeleteDetails data={data} />;
  }

  if (isImportJob(data)) {
    return <ImportDetails data={data} />;
  }

  return <>Job type not found</>;
};

export default JobDetailPage;
